import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "bg-white rounded-2xl sm:rounded-3xl border border-divider p-4 sm:p-7 shadow-sm space-y-6 text-left" }
const _hoisted_2 = {
  key: 0,
  class: "flex gap-2"
}
const _hoisted_3 = { class: "text-brown" }
const _hoisted_4 = { class: "flex justify-between border-b border-divider pb-4" }
const _hoisted_5 = { class: "text-base sm:text-lg font-bold text-brown" }
const _hoisted_6 = { class: "text-xs text-muted mt-0.5" }
const _hoisted_7 = { class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" }
const _hoisted_8 = { class: "block text-xs sm:text-sm font-bold text-brown mb-3" }
const _hoisted_9 = { class: "block text-xs sm:text-sm font-bold text-brown mb-3" }
const _hoisted_10 = { class: "block text-xs sm:text-sm font-bold text-brown mb-2" }
const _hoisted_11 = { class: "grid grid-cols-2 gap-2.5" }
const _hoisted_12 = { class: "block text-xs sm:text-sm font-bold text-brown mb-2" }
const _hoisted_13 = { class: "block text-xs sm:text-sm font-bold text-brown mb-2" }
const _hoisted_14 = { class: "grid grid-cols-2 bg-secondary p-1 rounded-xl h-[52px]" }
const _hoisted_15 = { class: "grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 pt-2" }
const _hoisted_16 = { class: "block text-xs font-bold text-brown mb-2" }
const _hoisted_17 = { class: "grid grid-cols-2 gap-2.5" }
const _hoisted_18 = { class: "block text-xs font-bold text-brown mb-3" }
const _hoisted_19 = { class: "block text-xs font-bold text-brown mb-3" }
const _hoisted_20 = { class: "block text-xs font-bold text-brown mb-2" }
const _hoisted_21 = { class: "grid grid-cols-[repeat(auto-fill,minmax(115px,1fr))] gap-x-4 gap-y-2.5 pt-1" }
const _hoisted_22 = { class: "pt-5 border-t border-divider flex flex-col sm:flex-row items-center justify-between gap-4" }

import { capitalize, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import DatePicker from "@/components/inputs/DatePicker.vue";
import AppSelect from "@/components/inputs/AppSelect.vue";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import { MONTHS_KEYS } from "@/constants/chineseCalendar";
import CheckBox from "@/components/inputs/CheckBox.vue";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { FavorableDateSelectionRequest } from "@/types/calculators";
import { formatLocalDate } from "@/utils/date";


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingsComponent',
  props: {
    showFullCard: { type: Boolean }
  },
  emits: ["submitForm"],
  setup(__props: any, { emit: __emit }) {



const mountains = [
  "null",
  "子",
  "癸",
  "丑",
  "艮",
  "寅",
  "甲",
  "卯",
  "乙",
  "辰",
  "巽",
  "巳",
  "丙",
  "午",
  "丁",
  "未",
  "坤",
  "申",
  "庚",
  "酉",
  "辛",
  "戌",
  "乾",
  "亥",
  "壬",
];
const calculators = ["tong_shu_60", "civilian_37", "imperial_67"];

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");
const emit = __emit;

// Form Params
const selectedCalculator = ref(calculators[0]);
const selectedMountain = ref(mountains[0]);
const latitude = ref<number | null>(null);
const longitude = ref<number | null>(null);
const timeZone = ref<string | null>(null);
const selectedMonth = ref<number>(new Date().getMonth());
const selectedYear = ref<number>(new Date().getFullYear());
const birthDate = ref<Date | null>(null);
const birthTime = ref<string | null>(null);
const isMale = ref<boolean>(true);
const usefulElements = ref({
  wood: false,
  fire: false,
  earth: false,
  metal: false,
  water: false,
});
const useTrueSolarTime = ref(false);

// Form Settings
const isLoading = ref(false);
const supportedYears = computed(() => {
  const yearsCount = 100;
  const startYear = selectedYear.value - yearsCount / 2;

  return Array.from({ length: yearsCount + 1 }, (_, i) => startYear + i);
});
const isFormValid = computed(() => {
  return (
    birthDate.value !== null &&
    birthTime.value !== null &&
    latitude.value !== null &&
    longitude.value !== null &&
    timeZone.value !== null
  );
});
const periodString = computed(() => {
  const year = selectedYear.value;
  const month = selectedMonth.value;

  // 1. Форматируем месяц, чтобы всегда было две цифры (например, '10' вместо '9')
  // В JS месяцы идут от 0 до 11, поэтому добавляем 1
  const formattedMonth = String(month + 1).padStart(2, "0");

  // 2. Находим последний день выбранного месяца
  // Передача 0 в качестве дня для следующего месяца возвращает последний день текущего
  const lastDay = new Date(year, month + 1, 0).getDate();
  const formattedLastDay = String(lastDay).padStart(2, "0");

  // 3. Собираем итоговую строку
  return `01.${formattedMonth}.${year} – ${formattedLastDay}.${formattedMonth}.${year}`;
});

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  latitude.value = payload.location.lat();
  longitude.value = payload.location.lng();
  timeZone.value = normalizeGoogleTimeZone(payload.timeZone);
}

function handleToggleUsefulElement(
  elementKey: keyof typeof usefulElements.value
) {
  usefulElements.value[elementKey] = !usefulElements.value[elementKey];
}

function handleSubmit() {
  const formattedMonth = String(selectedMonth.value + 1).padStart(2, "0");

  emit("submitForm", {
    requested_date: `${selectedYear.value}-${formattedMonth}-01`,
    birthday: formatLocalDate(birthDate.value as Date),
    is_male: isMale.value ? 1 : 0,
    latitude: latitude.value as number,
    longitude: longitude.value as number,
    timezone: timeZone.value as string,
    activity_scope: selectedCalculator.value,
    ...(selectedMountain.value !== "null" && {
      house_facing: selectedMountain.value,
    }),
    favourable_elements: Object.keys(usefulElements.value)
      .filter(
        (key) => usefulElements.value[key as keyof typeof usefulElements.value]
      )
      .map(capitalize),
    use_true_solar_time: useTrueSolarTime.value ? 1 : 0,
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!__props.showFullCard)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _cache[13] || (_cache[13] = _createElementVNode("span", { class: "text-muted" }, "Період:", -1)),
          _createElementVNode("strong", _hoisted_3, _toDisplayString(periodString.value), 1)
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", null, [
              _createElementVNode("h2", _hoisted_5, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.title")), 1),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.desc")), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", null, [
              _createElementVNode("label", _hoisted_8, _toDisplayString(_unref(t)("labels.build-year")), 1),
              _createVNode(AppSelect, {
                class: "w-full",
                items: supportedYears.value,
                "default-value": selectedYear.value,
                onSelectOption: _cache[0] || (_cache[0] = (newSelectedYear) => selectedYear.value = Number(newSelectedYear))
              }, null, 8, ["items", "default-value"])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("label", _hoisted_9, _toDisplayString(_unref(t)("labels.build-month")), 1),
              _createVNode(AppSelect, {
                class: "w-full",
                items: _unref(MONTHS_KEYS),
                "default-value": _unref(MONTHS_KEYS)[selectedMonth.value],
                onSelectOption: _cache[1] || (_cache[1] = (newSelectedMonth) => selectedMonth.value = _unref(MONTHS_KEYS).indexOf(newSelectedMonth))
              }, {
                option: _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(_unref(t)(`months.${item}`)), 1)
                ]),
                _: 1
              }, 8, ["items", "default-value"])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("label", _hoisted_10, _toDisplayString(_unref(t)("labels.birthday")), 1),
              _createVNode(DatePicker, {
                id: "dateFrom",
                onDateSelected: _cache[2] || (_cache[2] = (newDate) => birthDate.value = newDate)
              })
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", null, [
                _createElementVNode("label", _hoisted_12, _toDisplayString(_unref(t)("labels.birthtime")), 1),
                _withDirectives(_createElementVNode("input", {
                  id: "time",
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((birthTime).value = $event)),
                  type: "time",
                  class: "border border-divider pl-4 pr-[6px] w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted"
                }, null, 512), [
                  [_vModelText, birthTime.value]
                ])
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("label", _hoisted_13, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.gender.title")), 1),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["font-semibold shadow-sm rounded-lg text-xs transition-all", 
                  isMale.value ? 'bg-brown text-white' : 'text-muted hover:text-brown'
                ]),
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (isMale.value = true))
                  }, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.gender.male")), 3),
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["rounded-lg text-xs transition-all", 
                  isMale.value ? 'text-muted hover:text-brown' : 'bg-brown text-white'
                ]),
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (isMale.value = false))
                  }, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.gender.female")), 3)
                ])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("div", null, [
              _createElementVNode("label", _hoisted_16, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.true-solar-location")), 1),
              _createVNode(GooglePlaceAutocompleteInput, {
                styleType: "outline",
                onPlaceSelected: handlePlaceSelected
              })
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", null, [
                _createElementVNode("label", _hoisted_18, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.case-catalog")), 1),
                _createVNode(AppSelect, {
                  items: calculators,
                  defaultValue: selectedCalculator.value,
                  class: "w-full",
                  onSelectOption: _cache[6] || (_cache[6] = (newCalculator) => selectedCalculator.value = newCalculator)
                }, {
                  option: _withCtx(({ item }) => [
                    _createTextVNode(_toDisplayString(_unref(t)(`calculators.personal-date.settings-card.calc.${item}`)), 1)
                  ]),
                  _: 1
                }, 8, ["defaultValue"])
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("label", _hoisted_19, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.house-facade")), 1),
                _createVNode(AppSelect, {
                  items: mountains,
                  defaultValue: selectedMountain.value,
                  class: "w-full"
                }, {
                  option: _withCtx(({ item }) => [
                    _createTextVNode(_toDisplayString(item === "null"
                    ? _unref(t)(
                        "calculators.personal-date.settings-card.unknown-mountain"
                      )
                    : _unref(t)("calculators.personal-date.settings-card.mountain", {
                        mountain: item,
                      })), 1)
                  ]),
                  _: 1
                }, 8, ["defaultValue"])
              ])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("label", _hoisted_20, _toDisplayString(_unref(t)("calculators.personal-date.settings-card.useful-elements")), 1),
              _createElementVNode("div", _hoisted_21, [
                _createVNode(CheckBox, {
                  "input-id": "wood",
                  label: 
                _unref(t)('calculators.personal-date.settings-card.elements.wood')
              ,
                  onToggle: _cache[7] || (_cache[7] = ($event: any) => (handleToggleUsefulElement('wood'))),
                  class: "select-none !w-auto"
                }, null, 8, ["label"]),
                _createVNode(CheckBox, {
                  "input-id": "fire",
                  label: 
                _unref(t)('calculators.personal-date.settings-card.elements.fire')
              ,
                  onToggle: _cache[8] || (_cache[8] = ($event: any) => (handleToggleUsefulElement('fire'))),
                  class: "select-none !w-auto"
                }, null, 8, ["label"]),
                _createVNode(CheckBox, {
                  "input-id": "earth",
                  label: 
                _unref(t)('calculators.personal-date.settings-card.elements.earth')
              ,
                  onToggle: _cache[9] || (_cache[9] = ($event: any) => (handleToggleUsefulElement('earth'))),
                  class: "select-none !w-auto"
                }, null, 8, ["label"]),
                _createVNode(CheckBox, {
                  "input-id": "metal",
                  label: 
                _unref(t)('calculators.personal-date.settings-card.elements.metal')
              ,
                  onToggle: _cache[10] || (_cache[10] = ($event: any) => (handleToggleUsefulElement('metal'))),
                  class: "select-none !w-auto"
                }, null, 8, ["label"]),
                _createVNode(CheckBox, {
                  "input-id": "water",
                  label: 
                _unref(t)('calculators.personal-date.settings-card.elements.water')
              ,
                  onToggle: _cache[11] || (_cache[11] = ($event: any) => (handleToggleUsefulElement('water'))),
                  class: "select-none !w-auto"
                }, null, 8, ["label"])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_22, [
            _createVNode(CheckBox, {
              "input-id": "true_solar_time",
              label: 
            _unref(t)('calculators.personal-date.settings-card.use-true-solar-time')
          ,
              onToggle: _cache[12] || (_cache[12] = ($event: any) => (useTrueSolarTime.value = !useTrueSolarTime.value)),
              class: "select-none !w-auto"
            }, null, 8, ["label"]),
            _createVNode(GradientButton, {
              class: "w-[153px] h-[46px] md:w-[191px] md:h-14",
              "bg-color": "bg-brown-gradient",
              "hover-bg-color": "bg-orange-gradient",
              text: _unref(t)('buttons.calculate'),
              onClick: handleSubmit,
              "is-loading": isLoading.value,
              isDisabled: !isFormValid.value
            }, null, 8, ["text", "is-loading", "isDisabled"])
          ])
        ], 64))
  ]))
}
}

})